// React & Hooks
import { memo, useCallback, useEffect, useState } from "react";

// API
import {
  getInvoiceIncomingAPI,
  approveInvoiceIncomingAPI,
  rejectInvoiceIncomingAPI,
  markPaidInvoiceIncomingAPI,
  getInvoiceIncomingXmlAPI,
  getInvoiceIncomingAllocationsAPI,
} from "services/api/finance/incoming-invoices/api";
import { transformInvoiceIncomingEditForm, transformInvoiceIncoming } from "services/api/finance/incoming-invoices/transformations";
import { initialInvoiceIncomingEditForm, INVOICE_INCOMING_ALLOCATABLE_STATUSES } from "services/api/finance/incoming-invoices/consts";

// Helpers
import { useTranslate } from "helpers";

// Types
import {
  InvoiceIncomingEditFormValues,
  InvoiceIncoming,
  InvoiceIncomingDocument,
  InvoiceIncomingRejection,
  InvoiceIncomingCostAllocation,
} from "services/api/finance/incoming-invoices/types";
import { InvoiceIncomingPreviewCardProps } from "./types";

// UI Komponente
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import { PdfViewer } from "ui/components/PdfViewer";
import Tabs from "ui/components/Tabs/Tabs";
import { TextArea } from "ui/components/TextField/TextArea";
import InvoiceXmlPreview from "ui/components/InvoiceXmlPreview/InvoiceXmlPreview";

// Lokalne komponente
import InvoiceIncomingInfo from "./components/InvoiceIncomingInfo/InvoiceIncomingInfo";
import InvoiceIncomingDocuments from "./components/InvoiceIncomingDocuments/InvoiceIncomingDocuments";
import InvoiceIncomingPayments from "./components/InvoiceIncomingInfo/components/InvoiceIncomingPayments/InvoiceIncomingPayments";
import InvoiceIncomingStatusToolbar from "./components/InvoiceIncomingStatusToolbar/InvoiceIncomingStatusToolbar";
import InvoiceIncomingAllocationModal from "./components/InvoiceIncomingAllocationModal/InvoiceIncomingAllocationModal";
import InvoiceIncomingDecisionInfo from "./components/InvoiceIncomingDecisionInfo/InvoiceIncomingDecisionInfo";
import InvoiceIncomingOriginBadge from "./components/InvoiceIncomingOriginBadge/InvoiceIncomingOriginBadge";
import InvoiceIncomingRejectModal from "../InvoiceIncomingRejectModal/InvoiceIncomingRejectModal";

const InvoiceIncomingPreviewCard: React.FC<InvoiceIncomingPreviewCardProps> = (props) => {
  const { show, invoiceId, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoice, setInvoice] = useState<InvoiceIncoming | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [showAllocationModal, setShowAllocationModal] = useState<boolean>(false);
  const [allocations, setAllocations] = useState<InvoiceIncomingCostAllocation[]>([]);

  const [formValues, setFormValues] = useState<InvoiceIncomingEditFormValues>(
    initialInvoiceIncomingEditForm
  );

  useEffect(() => {
    if (!show) {
      setPdfUrl("");
      setShowPdf(false);
      setActiveDocId(null);
      return;
    }

    if (invoiceId) {
      fetchInvoice(invoiceId);
    } else {
      setInvoice(null);
      setFormValues(initialInvoiceIncomingEditForm);
      setPdfUrl("");
      setShowPdf(false);
      setActiveDocId(null);
    }
  }, [invoiceId, show]);

  const fetchInvoice = async (id: string): Promise<InvoiceIncoming | null> => {
    setIsLoading(true);

    let result;
    try {
      result = await getInvoiceIncomingAPI(id);
    } catch {
      setIsLoading(false);
      return null;
    }

    const inv = transformInvoiceIncoming(result);
    setInvoice(inv);
    setFormValues(transformInvoiceIncomingEditForm(result));

    // Vozila računa ne stoje na zaglavlju nego u rasporedu troškova.
    try {
      setAllocations(await getInvoiceIncomingAllocationsAPI(id));
    } catch {
      setAllocations([]);
    }

    const firstPdf = inv.documents?.find((d) => d.fileExtension === "pdf");
    if (firstPdf) {
      setPdfUrl(firstPdf.url);
      setShowPdf(true);
      setActiveDocId(firstPdf.id);
    }

    setIsLoading(false);

    return inv;
  };

  const handleDocumentClick = useCallback((doc: InvoiceIncomingDocument) => {
    if (doc.fileExtension === "pdf") {
      setPdfUrl(doc.url);
      setShowPdf(true);
      setActiveDocId(doc.id);
    } else {
      window.open(doc.url, "_blank");
    }
  }, []);

  const handlePrintPdf = useCallback(() => {
    if (!pdfUrl) return;
    const printWindow = window.open(pdfUrl, "_blank");
    if (printWindow) {
      printWindow.addEventListener("load", () => {
        printWindow.print();
      });
    }
  }, [pdfUrl]);

  const isFromProvider = invoice?.incomingSourceId === "provider";
  const hasItems = (invoice?.items?.filter(item => !item.deleted).length ?? 0) > 0;
  // Odluka je jednokratna — jednom javljena provideru se ne mijenja.
  const canApprove = !!invoice && invoice.status?.id === "received";
  // Troškovi nastaju tek iz odobrenog računa (plaćen i djelomično plaćen su prošli odobrenje).
  const canAllocate = INVOICE_INCOMING_ALLOCATABLE_STATUSES.includes(invoice?.status?.id ?? "");

  /**
   * Odluka je jednokratna: javljena provideru odnosno fiskalizirana kod Porezne se više ne mijenja.
   * Poslužitelj bi takvu promjenu odbio, pa se ovdje objasni prije klika, a ne nakon greške.
   */
  const decisionLockedReason = invoice?.rejectionFiscalizedAt
    ? "Odbijanje je fiskalizirano kod Porezne uprave — odluka se više ne mijenja."
    : invoice?.processStatusReportedAt
      ? "Odluka je javljena provideru — promjena zahtijeva ručno usklađenje s dobavljačem."
      : null;

  const handleStatusChange = useCallback(async (statusId: string) => {
    if (!invoiceId) return;

    // Odbijanje e-računa se fiskalizira kod Porezne, pa razlog tražimo prije slanja.
    if (statusId === "rejected" && isFromProvider) {
      setShowRejectModal(true);
      return;
    }

    try {
      switch (statusId) {
        case "approved":
          await approveInvoiceIncomingAPI(invoiceId, t);
          break;
        case "rejected":
          await rejectInvoiceIncomingAPI(invoiceId, null, t);
          break;
        case "paid":
          await markPaidInvoiceIncomingAPI(invoiceId, t);
          break;
        default:
          return;
      }

      const updated = await fetchInvoice(invoiceId);

      // Odobren račun ide u troškove, pa se raspodjela nudi odmah.
      if (statusId === "approved" && (updated?.items?.filter(item => !item.deleted).length ?? 0) > 0) {
        setShowAllocationModal(true);
      }
    } catch {
      // greška se prikazuje putem snackbara
    }
  }, [invoiceId, isFromProvider, t]);

  const handleApproveClick = useCallback(() => {
    handleStatusChange("approved");
  }, [handleStatusChange]);

  const handleRejectConfirm = useCallback(async (rejection: InvoiceIncomingRejection) => {
    if (!invoiceId) return;

    try {
      await rejectInvoiceIncomingAPI(invoiceId, rejection, t);
      setShowRejectModal(false);
      await fetchInvoice(invoiceId);
    } catch {
      // greška se prikazuje putem snackbara
    }
  }, [invoiceId, t]);

  const handleRejectModalHide = useCallback(() => {
    setShowRejectModal(false);
  }, []);

  const handleInvoiceRefresh = useCallback(() => {
    if (invoiceId) fetchInvoice(invoiceId);
  }, [invoiceId]);

  const handlePaymentModalOpen = useCallback(() => {
    setShowPaymentModal(true);
  }, []);

  const handleAllocationModalOpen = useCallback(() => {
    setShowAllocationModal(true);
  }, []);

  const handleAllocationModalHide = useCallback(() => {
    setShowAllocationModal(false);
  }, []);

  const handleAllocationModalSuccess = useCallback(() => {
    setShowAllocationModal(false);
    if (invoiceId) fetchInvoice(invoiceId);
  }, [invoiceId]);

  const handleClose = useCallback(() => {
    setPdfUrl("");
    setShowPdf(false);
    setActiveDocId(null);
    setShowAllocationModal(false);
    setShowRejectModal(false);
    onClose();
  }, [onClose]);

  if (!invoiceId) return null;

  return (
    <Modal show={show} onHide={handleClose} position="side" size="medium">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={`Ulazni račun ${invoice?.invoiceNumber ?? ""}`}
        badge={invoice && <InvoiceIncomingOriginBadge invoice={invoice} />}
        onHide={handleClose}
      />
      <ModalBody>
        {isLoading && (
          <div className="relative h-[calc(100vh-80px)]">
            <Loader isLoading={true}>{null}</Loader>
          </div>
        )}

        <div className={isLoading ? "hidden" : ""}>
          {/* Status toolbar */}
          <div className="px-4 mt-6">
            <InvoiceIncomingStatusToolbar
              currentStatusId={invoice?.status?.id ?? null}
              lockedReason={decisionLockedReason}
              onStatusChange={handleStatusChange}
              onPayClick={handlePaymentModalOpen}
            />
          </div>

          {/* Stanje prijave odluke */}
          {invoice && <InvoiceIncomingDecisionInfo invoice={invoice} />}

          {/* Tabovi */}
          <div className="flex mt-4 mb-6 px-4 min-h-[60vh] xl:min-h-[800px]">
            <Tabs
              tabs={[
                {
                  label: "Osnovne informacije",
                  content: (
                    <div className="px-4 pt-3 pb-4 border border-slate-200 -mt-[1px]">
                      {invoice && (
                        <InvoiceIncomingInfo
                          invoice={invoice}
                          allocations={allocations}
                          formValues={formValues}
                          setFormValues={setFormValues}
                        />
                      )}
                    </div>
                  ),
                },
                {
                  label: "Dokumenti",
                  content: (
                    <div className="px-4 py-4 border border-slate-200 -mt-[1px]">
                      <InvoiceIncomingDocuments
                        documents={invoice?.documents ?? []}
                        activeDocId={activeDocId}
                        onDocumentClick={handleDocumentClick}
                      />
                    </div>
                  ),
                },
                {
                  label: "Uplate",
                  content: (
                    <div className="px-4 py-4 border border-slate-200 -mt-[1px]">
                      {invoice?.id && (
                        <InvoiceIncomingPayments
                          invoiceId={invoice.id}
                          payments={invoice.payments ?? []}
                          remainingAmount={invoice.remainingAmount ?? 0}
                          onPaymentChange={handleInvoiceRefresh}
                          showModal={showPaymentModal}
                          setShowModal={setShowPaymentModal}
                        />
                      )}
                    </div>
                  ),
                },
                {
                  label: "Interna napomena",
                  content: (
                    <div className="px-4 py-4 border border-slate-200 -mt-[1px]">
                      <TextArea
                        name="internalNote"
                        label="Interna napomena"
                        value={invoice?.internalNote ?? ""}
                        rows={6}
                        disabled
                      />
                    </div>
                  ),
                },
                // XML postoji samo za e-račune zaprimljene kroz providera.
                ...(isFromProvider && invoice?.id
                  ? [{
                    label: "eRačun (XML)",
                    content: (
                      <div className="px-4 py-4 border border-slate-200 -mt-[1px]">
                        <InvoiceXmlPreview
                          invoiceId={invoice.id}
                          invoiceNumber={invoice.invoiceNumber}
                          fetchXml={getInvoiceIncomingXmlAPI}
                          description="UBL 2.1 zapis e-računa zaprimljenog kroz providera."
                          fileNamePrefix="eracun"
                          errorMessage="XML nije dostupan za ovaj račun."
                        />
                      </div>
                    ),
                  }]
                  : []),
              ]}
            />
          </div>

          {/* PdfViewer */}
          {pdfUrl && <PdfViewer url={pdfUrl} show={showPdf} />}
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="px-4 py-3 flex justify-between gap-3">
          <div className="flex gap-2">
            <Button variant="outlined" children="Zatvori" onClick={handleClose} />
            {showPdf && pdfUrl && (
              <Button
                startIcon="/media/icons/print.svg"
                variant="secondary"
                children="Ispiši račun"
                onClick={handlePrintPdf}
              />
            )}
          </div>
          <div className="flex gap-2">
            {hasItems && canAllocate && (
              // Dok raspodjele nema, gumb je taj koji zove na akciju — zato upozoravajuća boja.
              <Button
                variant={allocations.length === 0 ? "warning" : "secondary"}
                children="Raspodjeli troškove"
                onClick={handleAllocationModalOpen}
              />
            )}
            {canApprove && (
              <Button
                variant="primary"
                children="Odobri"
                onClick={handleApproveClick}
              />
            )}
          </div>
        </div>
      </ModalFooter>

      <InvoiceIncomingRejectModal
        show={showRejectModal}
        onHide={handleRejectModalHide}
        onConfirm={handleRejectConfirm}
        invoiceNumber={invoice?.invoiceNumber}
      />

      {invoice?.id && (
        <InvoiceIncomingAllocationModal
          show={showAllocationModal}
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoiceNumber}
          items={invoice.items}
          defaultVehicleId={invoice.vehicle?.id ?? null}
          onHide={handleAllocationModalHide}
          onSuccess={handleAllocationModalSuccess}
        />
      )}
    </Modal>
  );
};
export default memo(InvoiceIncomingPreviewCard);
